import styled from '@emotion/styled'
import { NonNullable } from '@pubstack/common/src/assertion'
import { LightStackHistory } from '@pubstack/common/src/stack'
import { useAtomValue } from 'jotai'
import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useOutletContext } from 'react-router-dom'
import { useFetchStackHistoryEntry, useLiveStackHistory } from '~/api/stacks.api'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { Tooltip } from '~/components/Tooltip'
import Table, { handleTableSearchAndSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { ViewStackFlyout } from '~/modules/pbs/sites/site/stacks/ViewStackFlyout'
import { AdmSitePageContext } from '~/modules/pbs/sites/site/useSitePageContext'
import { configurationState } from '~/state'

export const DateFormat = 'MMM dd, yyyy t'

const Header = styled.div`
  display: flex;
  margin-bottom: 12px;
`

const FilterAction = styled.form`
  display: flex;
`

const ActionsTableCell = styled(TableCell)`
  width: 0px;
`

const ButtonActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const DEVICES = ['desktop', 'mobile'] as const

const DownloadStackButtons = ({ stack, stackConfigUrl }: { stack: LightStackHistory; stackConfigUrl: string }) => (
  <TableCell>
    {DEVICES.map((device) => (
      <a key={device} href={`${stackConfigUrl}/${stack.stackGroupId}/${stack.version}/${device}.json`} download target={'_blank'} rel={'noreferrer'}>
        <Button variant={'tertiary'} iconName={device} onClick={(event) => event.stopPropagation()} trackingLabel={`adm-sites-stacks-${device}-config-download`} />
      </a>
    ))}
  </TableCell>
)

export const SiteHistory: FunctionComponent = () => {
  const { site, scopeId } = useOutletContext<AdmSitePageContext>()
  const { data: history, isFetching } = useLiveStackHistory(site.id)
  const fetchHistoryEntry = useFetchStackHistoryEntry()
  const { STACK_CONFIG_URL_PREFIX } = useAtomValue(configurationState)
  const stackConfigUrl = `${STACK_CONFIG_URL_PREFIX}/${scopeId.split('-')[0]}/${site.id.split('-')[0]}`

  const flyout = useGlobalFlyout()
  const { control } = useForm<{ search: string }>({
    defaultValues: { search: '' },
  })
  const search = useWatch({ control, name: 'search' })

  const [columns, setColumns] = useState<TableColumns<LightStackHistory>>(
    (
      [
        { name: 'Stack name', attributeSort: 'name', isSortable: true, order: 'none' },
        { name: 'Version', isSortable: false },
        { name: 'Deployment', isSortable: false },
        { name: 'Author', attributeSort: 'updatedBy', isSortable: true, order: 'none' },
        { name: 'Download', isSortable: false },
        { name: 'Date', attributeSort: 'updatedAt', isSortable: true, order: 'descending' },
        { name: 'Action', isSortable: false },
      ] as TableColumns
    ).filter(NonNullable)
  )

  const admHistory = (history ?? []).filter((s) => s.type === 'adm')
  const displayedHistory = handleTableSearchAndSort(columns, admHistory, search, ['name', 'updatedBy'])

  return (
    <>
      <Header>
        <FilterAction
          onSubmit={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        >
          <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={control} />
        </FilterAction>
      </Header>

      <Table columns={columns} isLoading={isFetching ?? false} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
        {displayedHistory.map((stack) => {
          const openDetailedView = async () => {
            // The flyout diffs output against snapshot, neither of which the list carries.
            const fullStack = await fetchHistoryEntry(stack.id)
            flyout.open(ViewStackFlyout, { color: Colors.King, width: `816px`, closeTrackingLabel: 'adm-sites-history-view-close' }, { site, stack: fullStack })
          }
          return (
            <TableRow
              key={stack.id}
              clickable={{
                trackingLabel: 'adm-sites-history-view',
                onClick: (event) => {
                  event.stopPropagation()
                  openDetailedView()
                },
              }}
            >
              <TableCell>{stack.name}</TableCell>
              <TableCell>v{stack.version}</TableCell>
              <TableCell>{Math.round(stack.ratio * 100)}%</TableCell>
              <TableCell>{stack.updatedBy}</TableCell>
              <DownloadStackButtons stack={stack} stackConfigUrl={stackConfigUrl} />
              <TableCell>{DateTime.fromJSDate(new Date(stack.updatedAt)).toFormat(DateFormat, { locale: 'en' })}</TableCell>
              <ActionsTableCell>
                <ButtonActions>
                  <Tooltip title={'View'}>
                    <Button
                      variant={'tertiary'}
                      iconName={'view'}
                      onClick={(event) => {
                        event.stopPropagation()
                        openDetailedView()
                      }}
                      trackingLabel={'adm-sites-history-icon-view'}
                    />
                  </Tooltip>
                </ButtonActions>
              </ActionsTableCell>
            </TableRow>
          )
        })}
      </Table>
    </>
  )
}
